// Settings — Phase 4 Plan 04-07 (DIS-20, UI-SPEC §Screen 10).
// Phase 12 Plan 12-10: migrated to @telegram-apps/telegram-ui (List+Section+Cell).
//
// Sections:
//   1. Language: radio rows (TR / BG / EN / SR). On change → PATCH /me +
//      i18next.changeLanguage(locale) + micro-toast. sr добавлен per 12-01/12-05.
//   2. Notifications: 2 switches (dispatcher_alerts + sound_critical).
//   3. Default visibility (owner-only): 6 toggles (eta/status locked, 4
//      togglable) + "Save defaults" → PATCH /api/v1/companies/me/default-visibility.
//   4. Destructive (red Section, UI-SPEC §8) — placeholder for account-level
//      destructive actions; surfaced as a distinct red Section per design.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Cell, List, Section, Switch } from "@telegram-apps/telegram-ui";
import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type NotificationPrefs,
  type UserMe,
  type VisibilitySettings,
  settingsApi,
} from "../api/settings";
import { useAuth } from "../auth/useAuth";
import { decodeJwtPayload } from "../lib/jwt";

const LANGUAGE_OPTIONS = [
  { value: "tr", labelKey: "dispatcher:settings.lang.tr" },
  { value: "bg", labelKey: "dispatcher:settings.lang.bg" },
  { value: "en", labelKey: "dispatcher:settings.lang.en" },
  // Phase 12 — sr (Serbian latinica) доступен owner/dispatcher (12-01/12-05).
  { value: "sr", labelKey: "dispatcher:settings.lang.sr" },
] as const;

const TOGGLEABLE_VISIBILITY_FIELDS = [
  "driver_name",
  "driver_contact",
  "license_plate",
  "carrier_name",
] as const;

export function Settings(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient, jwt } = useAuth();

  const role = useMemo(() => decodeJwtPayload(jwt)?.role ?? null, [jwt]);
  const isOwner = role === "owner";

  const meQuery = useQuery<UserMe>({
    queryKey: ["me"],
    queryFn: () => settingsApi.getMe(apiClient),
  });

  const queryClient = useQueryClient();

  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (toast === null) return;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  const updateMeMutation = useMutation({
    mutationFn: (updates: { preferred_locale?: string; notification_prefs?: NotificationPrefs }) =>
      settingsApi.patchMe(apiClient, updates),
    onSuccess: (data, vars) => {
      queryClient.setQueryData(["me"], data);
      if (vars.preferred_locale) {
        void i18next.changeLanguage(vars.preferred_locale);
        setToast(
          t("dispatcher:settings.lang.changed", {
            lang: vars.preferred_locale,
          }) as string,
        );
      }
    },
  });

  // --- Default visibility (owner-only section) ---

  const [visibilityDraft, setVisibilityDraft] = useState<VisibilitySettings | null>(null);

  const updateDefaultVisibilityMutation = useMutation({
    mutationFn: (updates: VisibilitySettings) =>
      settingsApi.patchCompanyDefaultVisibility(apiClient, updates),
    onSuccess: () => {
      setToast(
        t(
          "dispatcher:settings.visibility.saved",
          "Defaults saved. Existing trips are unchanged.",
        ) as string,
      );
      setVisibilityDraft(null);
    },
  });

  if (meQuery.isLoading) {
    return (
      <div data-testid="settings-loading" style={{ padding: 16 }}>
        {t("dispatcher:common.loading", "Loading…") as string}
      </div>
    );
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <div data-testid="settings-error" style={{ padding: 16 }}>
        {t("dispatcher:settings.error.load", "Failed to load settings.") as string}
      </div>
    );
  }

  const me = meQuery.data;
  const draft = visibilityDraft ?? {};
  const visibilityDirty = visibilityDraft !== null && Object.keys(visibilityDraft).length > 0;

  return (
    <List data-testid="settings" style={{ paddingBottom: 96 }}>
      {/* --- Language section --- */}
      <Section
        data-testid="settings-language-section"
        header={t("dispatcher:settings.section.language", "Language") as string}
      >
        {LANGUAGE_OPTIONS.map((opt) => (
          <Cell
            key={opt.value}
            Component="label"
            data-testid={`settings-lang-option-${opt.value}`}
            before={
              <input
                type="radio"
                name="language"
                value={opt.value}
                checked={me.preferred_locale === opt.value}
                disabled={updateMeMutation.isPending}
                onChange={() => {
                  updateMeMutation.mutate({ preferred_locale: opt.value });
                }}
                data-testid={`settings-lang-radio-${opt.value}`}
              />
            }
          >
            {t(opt.labelKey, opt.value) as string}
          </Cell>
        ))}
      </Section>

      {/* --- Notifications section --- */}
      <Section
        data-testid="settings-notifications-section"
        header={t("dispatcher:settings.section.notifications", "Notifications") as string}
      >
        <Cell
          Component="label"
          data-testid="settings-notif-dispatcher-alerts-row"
          after={
            <Switch
              data-testid="settings-notif-dispatcher-alerts"
              checked={me.notification_prefs.dispatcher_alerts !== false}
              disabled={updateMeMutation.isPending}
              onChange={(e) =>
                updateMeMutation.mutate({
                  notification_prefs: { dispatcher_alerts: e.target.checked },
                })
              }
            />
          }
        >
          {
            t(
              "dispatcher:settings.notif.dispatcher_alerts",
              "Dispatcher alerts (Telegram DM)",
            ) as string
          }
        </Cell>
        <Cell
          Component="label"
          data-testid="settings-notif-sound-critical-row"
          after={
            <Switch
              data-testid="settings-notif-sound-critical"
              checked={me.notification_prefs.sound_critical === true}
              disabled={updateMeMutation.isPending}
              onChange={(e) =>
                updateMeMutation.mutate({
                  notification_prefs: { sound_critical: e.target.checked },
                })
              }
            />
          }
        >
          {t("dispatcher:settings.notif.sound_critical", "Sound on critical alerts") as string}
        </Cell>
      </Section>

      {/* --- Default visibility (owner-only) --- */}
      {isOwner && (
        <Section
          data-testid="settings-default-visibility-section"
          header={
            t(
              "dispatcher:settings.section.default_visibility",
              "Default visibility for new trips",
            ) as string
          }
        >
          {/* Locked fields. */}
          {(["eta", "status"] as const).map((field) => (
            <Cell
              key={field}
              data-testid={`settings-vis-row-${field}`}
              subtitle={
                t("dispatcher:trip.visibility.locked_caption", "(locked by GDPR Art. 25)") as string
              }
              after={
                <input
                  type="checkbox"
                  checked
                  disabled
                  data-testid={`settings-vis-toggle-${field}`}
                  aria-label={field}
                />
              }
            >
              {t(`dispatcher:trip.visibility.${field}`, field) as string}
            </Cell>
          ))}
          {TOGGLEABLE_VISIBILITY_FIELDS.map((field) => {
            const current = typeof draft[field] === "boolean" ? (draft[field] as boolean) : true;
            return (
              <Cell
                key={field}
                Component="label"
                data-testid={`settings-vis-row-${field}`}
                after={
                  <Switch
                    data-testid={`settings-vis-toggle-${field}`}
                    checked={current}
                    onChange={(e) =>
                      setVisibilityDraft((d) => ({
                        ...(d ?? {}),
                        [field]: e.target.checked,
                      }))
                    }
                  />
                }
              >
                {t(`dispatcher:trip.visibility.${field}`, field) as string}
              </Cell>
            );
          })}
          <div style={{ padding: 16 }}>
            <Button
              data-testid="settings-vis-save"
              size="m"
              stretched
              disabled={!visibilityDirty || updateDefaultVisibilityMutation.isPending}
              onClick={() => {
                if (visibilityDraft !== null) {
                  updateDefaultVisibilityMutation.mutate(visibilityDraft);
                }
              }}
            >
              {t("dispatcher:settings.visibility.save_defaults", "Save defaults") as string}
            </Button>
          </div>
        </Section>
      )}

      {toast !== null && (
        <div
          data-testid="settings-toast"
          role="status"
          style={{
            position: "fixed",
            bottom: 96,
            left: 16,
            right: 16,
            padding: 12,
            background: "var(--tgui--button_color, var(--tg-theme-button-color))",
            color: "var(--tgui--button_text_color, var(--tg-theme-button-text-color))",
            borderRadius: 8,
            textAlign: "center",
            zIndex: 200,
          }}
        >
          {toast}
        </div>
      )}

      {updateMeMutation.isError && (
        <div data-testid="settings-update-error" style={{ padding: 16 }}>
          <Cell
            multiline
            style={{
              color: "var(--tgui--destructive_text_color, var(--tg-theme-destructive-text-color))",
            }}
          >
            {t("dispatcher:settings.error.save", "Failed to save.") as string}
          </Cell>
        </div>
      )}
    </List>
  );
}
